/**
 * Manages the cart bubble count display with special handling for bundled items.
 * Updates the count to show 1 per bundle group instead of 4 (main item + 3 customizations).
 * Works on all pages by fetching cart data from /cart.js API.
 */

class CartBubbleCounter {
  constructor() {
    this.bubbleElement = document.querySelector('[ref="cartBubbleCount"]');
    this.isUpdatingFromAPI = false; // Flag to prevent MutationObserver from re-triggering
    this.lastAPICall = 0; // Timestamp of last API call
    this.minAPIInterval = 500; // Minimum 500ms between API calls
    
    if (!this.bubbleElement) {
      console.warn('Cart bubble element not found');
      return;
    }

    // Update on page load
    this.updateCount();

    // Listen for cart updates from component-cart-items.js
    document.addEventListener('theme:cart:updated', () => {
      // Delay to ensure morphSection has completed
      setTimeout(() => this.updateCount(), 50);
    });
    
    // Also listen for other cart update events
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.updateCount(), 50);
    });
    
    // Listen for the CartUpdateEvent dispatched by component-cart-items.js
    document.addEventListener('cartUpdate', () => {
      setTimeout(() => this.updateCount(), 50);
    });

    // Listen for page visibility changes (when user returns to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Page became visible, recalculate count
        this.updateCount();
      }
    });

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', () => {
      setTimeout(() => this.updateCount(), 100);
    });
    
    // Use MutationObserver to catch any DOM changes that might clear the count
    const observer = new MutationObserver(() => {
      // Skip if we're in the middle of updating from API
      if (this.isUpdatingFromAPI) return;
      
      // If bubble is empty and shouldn't be, fix it
      if (this.bubbleElement && this.bubbleElement.textContent === '' && this.shouldHaveCount()) {
        this.updateCount();
      }
    });
    
    observer.observe(this.bubbleElement, {
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  /**
   * Check if we should have a cart count by looking at DOM or fetching from API
   */
  shouldHaveCount() {
    // First check if we're on cart page with DOM elements
    const totalItems = document.querySelectorAll('tr[data-key]').length;
    return totalItems > 0;
  }

  /**
   * Update count from either DOM (cart page) or API (all other pages)
   */
  updateCount() {
    if (!this.bubbleElement) return;
    
    // First try to get from DOM if on cart page
    const cartPageItems = document.querySelectorAll('tr[data-key]');
    if (cartPageItems.length > 0) {
      this.updateCountFromDOM(cartPageItems);
    } else {
      // On non-cart pages, fetch from API
      this.updateCountFromAPI();
    }
  }

  /**
   * Update count from cart table DOM (cart page only)
   */
  updateCountFromDOM(cartItems) {
    // Filter out gift with purchase items
    const nonGiftItems = Array.from(cartItems).filter(row => {
      // Check if row has _gift_with_purchase property
      const properties = row.querySelectorAll('.cart-items__properties dt');
      for (const prop of properties) {
        if (prop.textContent.trim() === '_gift_with_purchase:') {
          return false; // Exclude this item
        }
      }
      return true; // Include this item
    });
    
    const totalItems = nonGiftItems.length;
    
    if (totalItems === 0) {
      this.bubbleElement.textContent = '';
      return;
    }

    // Count one per cover: skip the wood/leather/hardware add-on rows (identified by
    // their own properties). Robust even if a row is missing its bundle-group tag.
    let displayCount = 0;
    nonGiftItems.forEach(row => {
      let isChild = false;
      row.querySelectorAll('.cart-items__properties dt').forEach(dt => {
        const t = dt.textContent.trim();
        if (t === 'Wood Type:' || t === 'Leather Color:' || t === 'Hardware Finish:') {
          isChild = true;
        }
      });
      if (!isChild) displayCount += 1;
    });

    this.bubbleElement.textContent = displayCount > 0 && displayCount < 100 ? String(displayCount) : '';
  }

  /**
   * Update count from Shopify cart API (for non-cart pages)
   */
  updateCountFromAPI() {
    // Throttle API calls to prevent rate limiting
    const now = Date.now();
    if (now - this.lastAPICall < this.minAPIInterval) {
      console.log('Skipping cart API call - too soon since last request');
      return;
    }
    
    this.lastAPICall = now;
    
    // Set flag to prevent MutationObserver from re-triggering
    this.isUpdatingFromAPI = true;
    
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartBubble = document.querySelector('[ref="cartBubble"]');
        
        if (!cart || !cart.items || cart.items.length === 0) {
          
          this.bubbleElement.innerHTML = '';
          // Hide the bubble if no items
          if (cartBubble) {
            cartBubble.classList.add('visually-hidden');
          }
          if (this.bubbleElement) {
            this.bubbleElement.classList.add('hidden');
          }
          this.isUpdatingFromAPI = false;
          return;
        }

        // Count one per cover: exclude the wood/leather/hardware add-on line items
        // (identified by their own properties) and gift-with-purchase items. Each
        // cover's main line item is counted; standalone products count by quantity.
        // Robust even if an add-on line item is missing its "Custom Cover" group tag.
        let displayCount = 0;
        cart.items.forEach(item => {
          const p = item.properties || {};
          if (p._gift_with_purchase === 'true') return;
          if (p['Wood Type'] || p['Leather Color'] || p['Hardware Finish']) return;
          displayCount += item.quantity;
        });

        // Set the count
        const countText = displayCount > 0 && displayCount < 100 ? String(displayCount) : '';
        this.bubbleElement.innerHTML = countText;
        
        // Make the bubble visible
        if (cartBubble) {
          cartBubble.classList.remove('visually-hidden');
        }
        
        // Remove hidden class from the count element
        if (this.bubbleElement) {
          this.bubbleElement.classList.remove('hidden');
        }
        
        // Monitor for clearing and re-apply if needed
        let checkCount = 0;
        const monitorInterval = setInterval(() => {
          checkCount++;
          if (checkCount > 50) {
            clearInterval(monitorInterval);
            this.isUpdatingFromAPI = false;
            return;
          }
          
          if (this.bubbleElement.innerHTML === '' && countText !== '') {
            this.isUpdatingFromAPI = true;
            this.bubbleElement.innerHTML = countText;
          }
          
          if (checkCount === 50) {
            clearInterval(monitorInterval);
            this.isUpdatingFromAPI = false;
          }
        }, 50);
      })
      .catch(error => {
        // Fallback to empty on error
        this.bubbleElement.innerHTML = '';
        this.isUpdatingFromAPI = false;
      });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartBubbleCounter());
} else {
  new CartBubbleCounter();
}
